import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Power, ArrowUp, ArrowDown, Pencil, Check, X } from "lucide-react";

const EXAM_TABS = ["LGS", "TYT", "AYT"];

export default function AdminCatalog() {
  const [tab, setTab] = useState("LGS");
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async (exam = tab) => {
    const { data } = await api.get("/admin/curriculum-catalog", { params: { exam_type: exam } });
    setItems(data);
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const onAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/admin/curriculum-catalog", { exam_type: tab, topic_name: name });
      toast.success("Konu kataloğa eklendi.");
      setNewName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
  };

  const startEdit = (it) => { setEditingId(it.catalog_id); setEditName(it.topic_name); };

  const saveEdit = async (it) => {
    try {
      await api.put(`/admin/curriculum-catalog/${it.catalog_id}`, { topic_name: editName.trim() });
      toast.success("Konu yeniden adlandırıldı.");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const onToggle = async (it) => {
    try {
      await api.put(`/admin/curriculum-catalog/${it.catalog_id}`, { is_active: !(it.is_active !== false) });
      toast.success(it.is_active !== false ? "Konu pasifleştirildi (kayıt korunur)." : "Konu tekrar aktifleştirildi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await api.post("/admin/curriculum-catalog/reorder", {
        exam_type: tab, ids: next.map((x) => x.catalog_id),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıralama kaydedilemedi");
      load();
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-catalog-page">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Müfredat Kataloğu</h1>
        <p className="text-sm text-slate-400 mt-1">
          JSON toplu import konu doğrulamasının referans listesi. Mevcut konulardan bağımsızdır;
          pasifleştirilen kayıtlar silinmez, yalnızca doğrulama dışı bırakılır.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#262626]" data-testid="admin-catalog-tabs">
        {EXAM_TABS.map((t) => (
          <button key={t} onClick={() => { setTab(t); setEditingId(null); }}
            data-testid={`admin-catalog-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 text-sm rounded-t-md transition-colors ${
              tab === t ? "bg-[#EA580C] text-white" : "text-slate-300 hover:bg-[#171717]"
            }`}>
            {t}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder={`Yeni ${tab} konu adı`}
          data-testid="admin-catalog-new-name"
          className="flex-1 px-3 py-2 rounded-md bg-[#171717] border border-[#262626] outline-none focus:border-[#EA580C]" />
        <button onClick={onAdd} disabled={!newName.trim()}
          data-testid="admin-catalog-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#EA580C] hover:bg-[#F97316] text-sm disabled:opacity-50">
          <Plus size={14} /> Ekle
        </button>
      </div>

      <div className="border border-[#262626] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#171717] text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left p-3 w-16">Sıra</th>
              <th className="text-left p-3">Konu Adı</th>
              <th className="text-left p-3 w-24">Durum</th>
              <th className="p-3 w-64" />
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const active = it.is_active !== false;
              const editing = editingId === it.catalog_id;
              return (
                <tr key={it.catalog_id}
                  data-testid={`admin-catalog-row-${it.catalog_id}`}
                  className={`border-t border-[#262626] hover:bg-[#171717] ${active ? "" : "opacity-60"}`}>
                  <td className="p-3 font-mono text-slate-500">{idx + 1}</td>
                  <td className="p-3">
                    {editing ? (
                      <input value={editName} onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveEdit(it)}
                        data-testid={`admin-catalog-edit-input-${it.catalog_id}`}
                        className="w-full px-2 py-1 rounded bg-[#0A0A0A] border border-[#EA580C] outline-none"
                        autoFocus />
                    ) : (
                      <span data-testid={`admin-catalog-name-${it.catalog_id}`}>{it.topic_name}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span data-testid={`admin-catalog-status-${it.catalog_id}`}
                      className={`inline-block px-2 py-0.5 rounded-full text-xs ${active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                      {active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {editing ? (
                      <>
                        <button onClick={() => saveEdit(it)}
                          data-testid={`admin-catalog-save-${it.catalog_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs mr-1">
                          <Check size={12} /> Kaydet
                        </button>
                        <button onClick={() => setEditingId(null)}
                          data-testid={`admin-catalog-cancel-${it.catalog_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] text-xs mr-1">
                          <X size={12} /> Vazgeç
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => move(idx, -1)} disabled={idx === 0}
                          data-testid={`admin-catalog-up-${it.catalog_id}`}
                          className="inline-flex items-center px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs mr-1 disabled:opacity-30">
                          <ArrowUp size={12} />
                        </button>
                        <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                          data-testid={`admin-catalog-down-${it.catalog_id}`}
                          className="inline-flex items-center px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs mr-1 disabled:opacity-30">
                          <ArrowDown size={12} />
                        </button>
                        <button onClick={() => startEdit(it)}
                          data-testid={`admin-catalog-edit-${it.catalog_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs mr-1">
                          <Pencil size={12} /> Adlandır
                        </button>
                        <button onClick={() => onToggle(it)}
                          data-testid={`admin-catalog-toggle-${it.catalog_id}`}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs ${
                            active
                              ? "border-[#262626] hover:border-[#EA580C]"
                              : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25"
                          }`}>
                          <Power size={12} /> {active ? "Pasifleştir" : "Aktifleştir"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan="4" className="p-8 text-center text-slate-500"
                  data-testid="admin-catalog-empty">
                  {tab} kataloğunda henüz konu yok.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
